import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BUSINESS_UNITS } from '@/constants/businessUnits';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, DollarSign, FileText, Calendar, Building, Printer, Wallet } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useExpense, useMarkExpensePaid } from '@/hooks/finance/useExpenses';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/currency';
import DocumentUpload from '@/components/finance/DocumentUpload';
import DocumentList from '@/components/finance/DocumentList';

const paymentStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pending', variant: 'outline' },
  paid: { label: 'Paid', variant: 'default' },
  cancelled: { label: 'Cancelled', variant: 'destructive' },
};

const ExpenseDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { data: expense, isLoading, refetch } = useExpense(id || '');
  const { data: bankAccounts, isLoading: bankAccountsLoading } = useBankAccounts(true);
  const markPaid = useMarkExpensePaid();

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [buAllocations, setBuAllocations] = useState<{ business_unit: string; percentage: number }[]>([]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data } = await (supabase as any)
        .from('expense_bu_allocations')
        .select('business_unit,percentage')
        .eq('expense_id', id);
      setBuAllocations(data || []);
    })();
  }, [id]);

  const canMarkPaid = expense?.payment_status !== 'paid';

  const handleMarkPaid = async () => {
    if (!id) return;
    await markPaid.mutateAsync({ 
      id, 
      payment_method: paymentMethod,
      payment_reference: paymentReference,
      bank_account_id: bankAccountId || undefined,
    });
    setPaymentDialogOpen(false);
    setPaymentMethod('');
    setPaymentReference('');
    setBankAccountId('');
  };

  const handleDocumentUploaded = async () => {
    refetch();
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!expense) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Expense not found</h2>
          <Button className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </Layout>
    );
  }

  const statusConfig = paymentStatusConfig[expense.payment_status || 'pending'] || paymentStatusConfig.pending;

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">{expense.expense_number}</h1>
                <Badge variant={statusConfig.variant}>
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="text-muted-foreground">{expense.description}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/finance/accounting/expenses/${id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            {canMarkPaid && (
              <Button onClick={() => setPaymentDialogOpen(true)}>
                <DollarSign className="h-4 w-4 mr-2" />
                Mark as Paid
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Expense Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Expense Date</h4>
                  <p className="mt-1">{format(new Date(expense.expense_date), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Reference Number</h4>
                  <p className="mt-1">{expense.reference_number || 'N/A'}</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
                <p className="mt-1">{expense.description}</p>
              </div>

              {expense.vendor_name && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Vendor / Payee</h4>
                  <p className="mt-1">{expense.vendor_name}</p>
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base Amount</span>
                  <span>{formatCurrency(expense.amount, expense.currency)}</span>
                </div>
                {expense.vat_amount && expense.vat_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      VAT {expense.vat_rate ? `(${expense.vat_rate}%)` : ''}
                    </span>
                    <span className="text-emerald-600 dark:text-emerald-400">+ {formatCurrency(expense.vat_amount, expense.currency)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Total Expense</span>
                  <span>{formatCurrency(expense.total_amount, expense.currency)}</span>
                </div>
                {expense.withholding_amount && expense.withholding_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Withholding Tax {expense.withholding_rate ? `(${expense.withholding_rate}%)` : ''}
                    </span>
                    <span className="text-destructive">- {formatCurrency(expense.withholding_amount, expense.currency)}</span>
                  </div>
                )}
                {expense.net_payment && (
                  <>
                    <Separator />
                    <div className="flex justify-between text-lg font-bold">
                      <span>Net Payment</span>
                      <span>{formatCurrency(expense.net_payment, expense.currency)}</span>
                    </div>
                  </>
                )}
              </div>

              {expense.payment_status === 'paid' && (
                <>
                  <Separator />
                  <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                    <h4 className="font-medium">Payment Information</h4>
                    <div className="grid gap-2 text-sm">
                      {expense.payment_date && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Payment Date</span>
                          <span>{format(new Date(expense.payment_date), 'MMM d, yyyy')}</span>
                        </div>
                      )}
                      {expense.payment_method && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Method</span>
                          <span className="capitalize">{expense.payment_method.replace('_', ' ')}</span>
                        </div>
                      )}
                      {expense.bank_account && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Paid From</span>
                          <span>{expense.bank_account.name} {expense.bank_account.bank_name ? `(${expense.bank_account.bank_name})` : ''}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Payment Reference</span>
                        <span>{expense.payment_reference || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Department</p>
                    <p className="font-medium">{expense.department?.name || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building className="h-4 w-4 text-muted-foreground mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Business Unit</p>
                    {buAllocations.length > 0 ? (
                      <div className="mt-1 space-y-1">
                        {buAllocations
                          .slice()
                          .sort((a, b) => b.percentage - a.percentage)
                          .map((a) => {
                            const bu = BUSINESS_UNITS.find((b) => b.value === a.business_unit);
                            return (
                              <div key={a.business_unit} className="flex items-center gap-2 text-sm">
                                <span
                                  className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                                  style={{ background: bu?.color || '#999' }}
                                />
                                <span className="flex-1 font-medium">{bu?.label || a.business_unit}</span>
                                <span className="tabular-nums text-muted-foreground">{Number(a.percentage)}%</span>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="font-medium">
                        {(() => {
                          const v = (expense as any).business_unit;
                          const bu = BUSINESS_UNITS.find((b) => b.value === v);
                          return bu?.label || v || 'Not specified';
                        })()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{expense.category?.name || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="font-medium">
                      {expense.created_at ? format(new Date(expense.created_at), 'MMM d, yyyy') : 'N/A'}
                    </p>
                  </div>
                </div>
                {expense.bank_account && (
                  <div className="flex items-center gap-3">
                    <Wallet className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Bank Account</p>
                      <p className="font-medium">{expense.bank_account.name}</p>
                    </div>
                  </div>
                )}
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="text-2xl font-bold">{formatCurrency(expense.total_amount, expense.currency)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Documents Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <DocumentUpload
                  bucketName="expense-receipts"
                  entityId={expense.id}
                  entityType="expense"
                  documentTypes={[
                    { value: 'receipt', label: 'Receipt' },
                    { value: 'invoice', label: 'Invoice' },
                    { value: 'supporting_document', label: 'Supporting Document' },
                  ]}
                  onUploadComplete={handleDocumentUploaded}
                />
                {expense.documents && expense.documents.length > 0 && (
                  <DocumentList
                    documents={expense.documents.map(d => ({
                      id: d.id,
                      file_name: d.file_name,
                      file_path: d.file_path,
                      document_type: d.document_type,
                      created_at: d.created_at,
                    }))}
                    bucketName="expense-receipts"
                    onDelete={handleDocumentUploaded}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Mark as Paid Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Expense as Paid</DialogTitle>
            <DialogDescription>
              Confirm payment of {formatCurrency(expense.net_payment || expense.total_amount, expense.currency)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Paid From Account</Label>
              <Select value={bankAccountId} onValueChange={setBankAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder={bankAccountsLoading ? "Loading..." : "Select bank account"} />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts?.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name} {account.bank_name ? `(${account.bank_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="petty_cash">Petty Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Reference</Label>
              <Input
                placeholder="Transaction ID or reference"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleMarkPaid} disabled={markPaid.isPending}>
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ExpenseDetailPage;
